import { useState, useRef, useCallback } from 'react';
import { getFormConfig } from '../config/formRegistry';

/**
 * Custom hook for managing form state and operations
 * Extracts common form logic from Home.js for reusability
 *
 * @param {string} formName - The name of the form
 * @param {object} portalUser - The authenticated user
 * @returns {object} Form management functions and state
 */
export const useFormManager = (formName, portalUser) => {
  const [payload, setPayload] = useState(null);
  const [formError, setFormError] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  // File upload states for newHire form
  const [idBadgeFile, setIdBadgeFile] = useState(null);
  const [govIdFront, setGovIdFront] = useState(null);
  const [govIdRear, setGovIdRear] = useState(null);
  const [ssnCard, setSsnCard] = useState(null);

  // Signature pad refs
  const managerPadRef = useRef({});
  const employeePadRef = useRef({});

  const config = getFormConfig(formName);

  /**
   * Clear manager signature pad
   */
  const clearManagerPad = useCallback((e) => {
    if (e) e.preventDefault();
    managerPadRef.current?.clear();
  }, []);

  /**
   * Clear employee signature pad
   */
  const clearEmployeePad = useCallback((e) => {
    if (e) e.preventDefault();
    employeePadRef.current?.clear();
  }, []);

  /**
   * Validate file uploads for newHire form
   * @returns {boolean} True if all files present, false otherwise
   */
  const validateFiles = useCallback(() => {
    if (!config?.requiresFiles) return true;

    let isValid = true;

    if (!idBadgeFile) {
      setErrMsg('Please select an id badge file.');
      isValid = false;
    }
    if (!govIdFront) {
      setErrMsg('Please upload front side of gov id card.');
      isValid = false;
    }
    if (!govIdRear) {
      setErrMsg('Please upload rear side of gov id card.');
      isValid = false;
    }
    if (!ssnCard) {
      setErrMsg('Please upload ssn card.');
      isValid = false;
    }

    return isValid;
  }, [config, idBadgeFile, govIdFront, govIdRear, ssnCard]);

  /**
   * Process newHire payload with file uploads
   * @returns {FormData} FormData object with files and JSON payload
   */
  const processNewHirePayload = useCallback(() => {
    if (!validateFiles()) return null;

    const formdata = new FormData();
    const blob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });

    formdata.append('newHireInfoJSON', blob, 'newHireInfo');
    formdata.append('idBadge', idBadgeFile, 'idBadge');
    formdata.append('govIdFront', govIdFront, 'govIdFront');
    formdata.append('govIdRear', govIdRear, 'govIdRear');
    formdata.append('ssnCard', ssnCard, 'ssnCard');

    return formdata;
  }, [payload, idBadgeFile, govIdFront, govIdRear, ssnCard, validateFiles]);

  /**
   * Build final payload with signatures
   * @returns {object|FormData} Final payload ready for submission
   */
  const buildFinalPayload = useCallback(() => {
    if (!payload || !portalUser) return null;

    // Handle newHire form with files
    if (formName === 'newHire') {
      return processNewHirePayload();
    }

    // Start with base payload
    let finalPayload = { ...payload, portalUser };

    // Add signatures based on form configuration
    if (
      config?.requiresManagerSignature &&
      !config?.requiresEmployeeSignature
    ) {
      // Single manager signature (most forms)
      finalPayload.sig = managerPadRef.current
        ?.getTrimmedCanvas()
        ?.toDataURL('image/png');
    }

    if (config?.requiresEmployeeSignature) {
      // TimeAdjustment has both signatures
      finalPayload.managerSig = managerPadRef.current
        ?.getTrimmedCanvas()
        ?.toDataURL('image/png');
      finalPayload.employeeSig = employeePadRef.current
        ?.getTrimmedCanvas()
        ?.toDataURL('image/png');
    }

    return finalPayload;
  }, [payload, portalUser, formName, config, processNewHirePayload]);

  /**
   * Check if form can be submitted
   * @returns {boolean} True if form is ready for submission
   */
  const canSubmit = useCallback(() => {
    if (formError) {
      setErrMsg(
        'Please fill in all required fields correctly before submitting.'
      );
      return false;
    }

    if (config?.requiresFiles && !validateFiles()) {
      return false;
    }

    return true;
  }, [formError, config, validateFiles]);

  return {
    // State
    payload,
    setPayload,
    formError,
    setFormError,
    errMsg,
    setErrMsg,

    // File uploads (for newHire)
    idBadgeFile,
    setIdBadgeFile,
    govIdFront,
    setGovIdFront,
    govIdRear,
    setGovIdRear,
    ssnCard,
    setSsnCard,

    // Signature pads
    managerPadRef,
    employeePadRef,
    clearManagerPad,
    clearEmployeePad,

    // Functions
    buildFinalPayload,
    canSubmit,
    validateFiles,

    // Config
    config,
  };
};
